import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthUser, Rol } from '@/types/auth';

/**
 * AuthStore — única fuente de verdad del estado de autenticación.
 *
 * Persistencia: localStorage (clave 'gsh.auth'). Aceptable para un panel web
 * corporativo en LAN; si más adelante se requiere mayor seguridad podemos
 * migrar a `sessionStorage` o usar httpOnly cookies con refresh-token.
 */
interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;

  setAuth: (token: string, user: AuthUser) => void;
  logout: () => void;

  /** True si el rol del usuario está dentro de los permitidos. */
  hasRole: (roles: Rol[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,

      setAuth: (token, user) => set({ token, user, isAuthenticated: true }),

      logout: () => set({ token: null, user: null, isAuthenticated: false }),

      hasRole: (roles) => {
        const user = get().user;
        return !!user && roles.includes(user.rol);
      },
    }),
    {
      name: 'gsh.auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ token: s.token, user: s.user, isAuthenticated: s.isAuthenticated }),
    },
  ),
);
